import { networkApi } from '../infrastructure/api/NetworkApiClient';

interface TransformerAmqpImportResult {
  message: string;
  id: string;
  deviceUuid: string;
  deviceSgtin: string;
  trafoName: string | null;
  ratingPowerKva: number | null;
  latitude: number;
  longitude: number;
  outletsCount: number;
  valueMappingsCount: number;
  linkedTransformerStationId: string | null;
}

interface TransformerAmqpDevice {
  id: string;
  deviceUuid: string;
  deviceSgtin: string;
  gatewaySgtin: string | null;
  latitude: number;
  longitude: number;
  trafoName: string | null;
  ratingPowerKva: number | null;
  linkedTransformerStationId: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
}

interface MeasurementValue {
  timestamp: string;
  value: number;
  valueId: number;
}

interface MeasurementImportResult {
  message: string;
  insertedCount: number;
  rawValuesCount: number;
}

interface OutletMeasurement {
  valueId: number;
  value: number;
  timestamp: string;
  unit: string | null;
  measurementType: string | null;
  iecName: string | null;
  bedeutung: string | null;
  einheit: string | null;
}

interface LinkedAmqpOutlet {
  id: string;
  slot: number | null;
  portName: string | null;
  valuePrefix: string;
  name: string | null;
  deviceFamily: string | null;
  deviceTypeId: string | null;
  fuseSizeA: number | null;
  ratingPowerKva: number | null;
  thresholdWarningPercent: number | null;
  thresholdCriticalPercent: number | null;
  linkedCableId: string | null;
  measurements: OutletMeasurement[];
}

interface LinkedAmqpOutletsResponse {
  stationId: string;
  deviceSgtin: string;
  lastMessageAt: string | null;
  ratingPowerKva?: number | null;
  trafoOutletId?: string | null;
  outlets: LinkedAmqpOutlet[];
}

/**
 * Import Lemonbeat metadata from XML string
 */
export async function importMetadata(xmlContent: string): Promise<TransformerAmqpImportResult> {
  // Note: This uses a special content type, so we use the raw client method
  return networkApi.zonePost<TransformerAmqpImportResult>(
    '/transformer-amqp/metadata',
    xmlContent
  );
}

/**
 * Import Lemonbeat metadata from file (extracts first XML block from log file)
 */
export async function importMetadataFile(file: File): Promise<TransformerAmqpImportResult> {
  const content = await file.text();

  // Extract first <lsbl...>...</lsbl> block from log file
  const match = content.match(/<lsbl[^>]*>[\s\S]*?<\/lsbl>/);
  if (!match) {
    throw new Error('Keine gültigen XML-Metadaten in der Datei gefunden');
  }

  return importMetadata(match[0]);
}

/**
 * Get all AMQP devices
 */
export async function getDevices(): Promise<TransformerAmqpDevice[]> {
  return networkApi.zoneGet<TransformerAmqpDevice[]>('/transformer-amqp/devices');
}

/**
 * Import measurements for a device
 */
export async function importMeasurements(
  deviceSgtin: string,
  values: MeasurementValue[]
): Promise<MeasurementImportResult> {
  return networkApi.zonePost<MeasurementImportResult>(
    '/transformer-amqp/measurements',
    { deviceSgtin, values }
  );
}

/**
 * Get linked AMQP outlets for a trafostation
 */
export async function getLinkedOutletsByTrafo(trafoId: string): Promise<LinkedAmqpOutletsResponse | null> {
  // No station linked to this trafo (the normal case) answers 204 No Content -> null.
  // Backends before 1.88.2 answered 404; that stays an expected (unlogged) status.
  const linked = await networkApi.zoneGet<LinkedAmqpOutletsResponse | ''>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets`,
    { expectedStatuses: [404] }
  );
  return linked && typeof linked === 'object' ? linked : null;
}

/**
 * Link an AMQP outlet to a cable
 */
export async function linkOutletToCable(
  trafoId: string,
  outletId: string,
  cableId: string
): Promise<{ message: string }> {
  return networkApi.zonePost<{ message: string }>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets/${encodeURIComponent(outletId)}/link`,
    { cableId }
  );
}

/**
 * Set the outlet used as trafo measurements source
 */
export async function setTrafoOutlet(
  trafoId: string,
  outletId: string
): Promise<{ message: string; outletId: string }> {
  return networkApi.zonePost<{ message: string; outletId: string }>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets/${encodeURIComponent(outletId)}/trafo`
  );
}

/**
 * Clear the selected trafo outlet
 */
export async function clearTrafoOutlet(
  trafoId: string
): Promise<{ message: string }> {
  return networkApi.zoneDelete<{ message: string }>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets/trafo`
  );
}

/**
 * Unlink an AMQP outlet from a cable
 */
export async function unlinkOutletFromCable(
  trafoId: string,
  outletId: string
): Promise<{ message: string }> {
  return networkApi.zoneDelete<{ message: string }>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets/${encodeURIComponent(outletId)}/link`
  );
}

// Historical data types
export type TimeRange = '24h' | '7d' | '30d';

export interface HourlyDataPoint {
  hourTimestamp: string;
  minValue: number;
  maxValue: number;
  measurementCount: number;
}

export interface MeasurementSeries {
  valueId: number;
  measurementType: string | null;
  iecName: string | null;
  unit: string | null;
  bedeutung: string | null;
  dataPoints: HourlyDataPoint[];
}

export interface OutletHistoryResponse {
  outletId: string;
  outletName: string | null;
  series: MeasurementSeries[];
}

/**
 * Get historical measurement data for an outlet
 */
export async function getOutletHistory(
  trafoId: string,
  outletId: string,
  timeRange: TimeRange = '24h'
): Promise<OutletHistoryResponse> {
  return networkApi.zoneGet<OutletHistoryResponse>(
    `/transformer-amqp/linked/${encodeURIComponent(trafoId)}/outlets/${encodeURIComponent(outletId)}/history?timeRange=${timeRange}`
  );
}

export const transformerAmqpService = {
  importMetadata,
  importMetadataFile,
  getDevices,
  importMeasurements,
  getLinkedOutletsByTrafo,
  linkOutletToCable,
  unlinkOutletFromCable,
  setTrafoOutlet,
  clearTrafoOutlet,
  getOutletHistory
};

export type {
  TransformerAmqpImportResult,
  TransformerAmqpDevice,
  MeasurementValue,
  MeasurementImportResult,
  OutletMeasurement,
  LinkedAmqpOutlet,
  LinkedAmqpOutletsResponse
};

export default transformerAmqpService;
